import { useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { ArrowLeft, Check, KeyRound, Loader2, Lock, Sparkles, WifiOff } from "lucide-react";
import { api } from "@/App";

const STORE = "kk_checkout";
const ACCENT = { denoise: "#00e5ff", skin: "#ff7a9c", texture: "#ff6a3d", reactor: "#c48bff", deflicker: "#a3ff4f", bundle: "#f2f4f8",
  genalpha: "#e69d27", plan: "#e69d27", topup: "#e69d27", recast: "#4ff2c2", aipack: "#4ff2c2" };

export function goToCheckout(body) {
  sessionStorage.setItem(STORE, JSON.stringify({ ...body, back: window.location.pathname + "#pricing" }));
  window.location.href = "/checkout";
}

const money = (cents, cur) => (cents == null ? "…" : new Intl.NumberFormat(undefined, { style: "currency", currency: (cur || "eur").toUpperCase() }).format(cents / 100));
const errText = (e) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't start the checkout. Please try again.");

export const CheckoutHeader = ({ back, badge = "secure checkout", Icon = Lock }) => (
  <header className="hair-border border-x-0 border-t-0 bg-[#121418]/80 backdrop-blur-md">
    <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
      <a href="/" className="font-display font-semibold text-xl uppercase leading-none" style={{ letterSpacing: "0.26em" }} data-testid="checkout-brand-link">KolorKlinic</a>
      <span className="hidden sm:block"><span className="chip flex items-center gap-1.5"><Icon size={11} /> {badge}</span></span>
      <a href={back || "/#pricing"} className="ml-auto text-xs text-[#9ba3b4] hover:text-white flex items-center gap-1.5 transition-colors" data-testid="checkout-back-link"><ArrowLeft size={13} /><span className="hidden sm:inline">Back to the site</span><span className="sm:hidden">Back</span></a>
    </div>
  </header>
);

const Row = ({ label, value, testid, strong }) => (
  <div className={`flex justify-between gap-4 ${strong ? "text-white font-semibold text-base pt-3 border-t border-[#2a2f3d]" : "text-sm text-[#9ba3b4]"}`}>
    <span>{label}</span><span data-testid={testid}>{value}</span>
  </div>
);

const OFFLINE_KINDS = ["denoise", "skin", "texture", "reactor", "deflicker", "bundle"];

const Trust = ({ kind }) => (
  <ul className="space-y-2 text-xs text-[#9ba3b4]" data-testid="checkout-trust">
    <li className="flex gap-2"><Lock size={13} className="mt-0.5 shrink-0" /> Secure payment by Stripe. Card details never touch our servers.</li>
    {OFFLINE_KINDS.includes(kind)
      ? <li className="flex gap-2"><WifiOff size={13} className="mt-0.5 shrink-0" /> Offline licence: the plugins run fully offline once activated.</li>
      : <li className="flex gap-2"><Sparkles size={13} className="mt-0.5 shrink-0" /> AI runs on your own fal.ai key: you pay fal.ai directly, no markup.</li>}
    <li className="flex gap-2"><KeyRound size={13} className="mt-0.5 shrink-0" /> Licence keys by email, the moment payment completes.</li>
  </ul>
);

function Summary({ s, totals }) {
  const accent = ACCENT[s.kind] || "#00e5ff";
  const per = s.interval ? ` / ${s.interval}` : "";
  const sub = totals?.subtotal ?? s.unit_amount * s.quantity;
  const known = totals?.tax_known && totals.tax != null;
  const tax = known ? money(totals.tax, s.currency) : "from your billing address";
  const name = s.label.replace("KolorKlinic ", "");
  const line = s.quantity > 1 ? `${s.quantity} ${s.kind === "topup" ? "minutes" : "seats"} × ${money(s.list_unit_amount, s.currency)}` : `${name}${s.interval ? "" : " licence"}`;
  return (
    <aside className="lg:sticky lg:top-8 space-y-6" data-testid="checkout-summary">
      <div className="h-1 w-16" style={{ background: accent }} />
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#9ba3b4] mb-2">Your order</div>
        <h1 className="font-display uppercase text-4xl leading-none" data-testid="checkout-product-name">{s.label.replace("KolorKlinic ", "")}</h1>
        <div className="text-sm text-[#9ba3b4] mt-2">KolorKlinic · OFX for DaVinci Resolve</div>
      </div>
      <ul className="space-y-2" data-testid="checkout-includes">
        {s.includes.map((t) => <li key={t} className="flex gap-2 text-sm text-[#d4d8e0]"><Check size={15} className="mt-0.5 shrink-0" style={{ color: accent }} />{t}</li>)}
      </ul>
      <div className="space-y-2 border border-[#2a2f3d] bg-[#0e1013] p-5">
        <Row label={line} value={`${money(s.list_unit_amount * s.quantity, s.currency)}${per}`} testid="checkout-line" />
        {s.discount_pct > 0 && <Row label={`Team discount (${s.discount_pct}%)`} value={`−${money((s.list_unit_amount - s.unit_amount) * s.quantity, s.currency)}`} testid="checkout-discount" />}
        <Row label="Subtotal" value={money(sub, s.currency)} testid="checkout-subtotal" />
        {totals?.discount > 0 && <Row label={totals.promo_code ? `Promo ${totals.promo_code}` : "Promo code"} value={`−${money(totals.discount, s.currency)}`} testid="checkout-promo" />}
        <Row label="Tax" value={tax} testid="checkout-tax" />
        <Row label="Total" value={known ? `${money(totals.total, s.currency)}${per}` : `${money(sub - (totals?.discount || 0), s.currency)} + tax`} testid="checkout-total" strong />
      </div>
      <p className="text-xs text-[#9ba3b4]" data-testid="checkout-promo-hint">Have a promo code? Add it in the payment form. Stripe may also offer to pay in your local currency.</p>
      <Trust kind={s.kind} />
    </aside>
  );
}

function useCheckout() {
  const [state, setState] = useState({});
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const body = JSON.parse(sessionStorage.getItem(STORE) || "null");
    if (!body) { setState({ err: "Nothing to check out. Pick a product on the site first." }); return; }
    const { back, session: cached, ...order } = body;
    const reuse = cached ? api.get(`/store/checkout/${cached.session_id}/totals`).then((r) => (r.data.status === "open" ? cached : null)).catch(() => null) : Promise.resolve(null);
    const create = () => api.post("/store/checkout", { ...order, embedded: true, origin_url: window.location.origin }).then((r) => {
      sessionStorage.setItem(STORE, JSON.stringify({ ...body, session: r.data }));
      return r.data;
    });
    Promise.all([api.get("/store/config"), reuse.then((c) => c || create())])
      .then(([c, data]) => setState({ back, stripe: loadStripe(c.data.publishable_key), ...data }))
      .catch((e) => setState({ back, err: errText(e) }));
  }, []);
  return state;
}

function useTotals(sid) {
  const [t, setT] = useState(null);
  useEffect(() => {
    if (!sid) return;
    let id;
    const tick = () => api.get(`/store/checkout/${sid}/totals`).then((r) => { setT(r.data); if (r.data.status !== "open") clearInterval(id); }).catch(() => {});
    tick();
    id = setInterval(tick, 4000);
    return () => clearInterval(id);
  }, [sid]);
  return t;
}

export default function CheckoutPage() {
  const c = useCheckout();
  const totals = useTotals(c.session_id);
  return (
    <div className="min-h-screen bg-[#0b0c0e] text-white" data-testid="checkout-page">
      <CheckoutHeader back={c.back} />
      <main className="max-w-6xl mx-auto px-6 py-12 grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-12">
        {c.err && <p className="text-sm text-[#ff7a7a] lg:col-span-2" data-testid="checkout-error">{c.err}</p>}
        {!c.err && !c.client_secret && <div className="flex items-center gap-2 text-sm text-[#9ba3b4] lg:col-span-2" data-testid="checkout-loading"><Loader2 size={16} className="animate-spin" /> Preparing your secure checkout…</div>}
        {c.summary && <Summary s={c.summary} totals={totals} />}
        {c.client_secret && (
          <section className="min-h-[560px]" data-testid="checkout-stripe-form">
            <div className="text-xs uppercase tracking-[0.2em] text-[#9ba3b4] mb-3">Payment</div>
            <EmbeddedCheckoutProvider stripe={c.stripe} options={{ clientSecret: c.client_secret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </section>
        )}
      </main>
    </div>
  );
}
